<template>
  <div v-if="companyItemTypes">
    <table class="table is-bordered is-narrow is-fullwidth">
      <thead>
        <tr class="no-border"
          :class="`summary-background-text-${$theme.name}`">
          <th class="is-title">Category</th>
          <th v-for="(assessment) in allAssessments"
            :key="assessment.assessmentID"
            class="has-text-right is-subtotal">
            {{ assessment.subQuoteNo === 0 ? 'Main' : `Supp-${assessment.subQuoteNo}` }}
          </th>
          <th v-if="allAssessments.length > 1"
            class="has-text-right is-subtotal">
            Summary
          </th>
          <th v-if="allAssessments.length === 0"
            class="has-text-right">Main</th>
        </tr>
      </thead>

      <tfoot class="no-border">
        <tr class="has-border-bottom-4"
          :class="[`summary-background-text-${$theme.name}`]">
          <td>Total <span class="subtext">(Ex GST)</span></td>
          <td v-for="(assessment) in allAssessments"
            :key="assessment.assessmentID"
            class="has-text-right">
            {{ $filters.formatCurrency(getAssessmentTotal(assessment), $userInfo.locale) }}
          </td>
          <td v-if="allAssessments.length > 1"
            class="has-text-right">{{ $filters.formatCurrency(getGrandAssessmentTotal(), $userInfo.locale) }}
          </td>
          <td v-if="allAssessments.length === 0"
            class="has-text-right">{{ $filters.formatCurrency(0, $userInfo.locale) }}</td>
        </tr>
        <tr v-if="value.discount">
          <td>Discount</td>
          <template v-if="allAssessments.length > 1">
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.discount, $userInfo.locale) }}</td>
            <td v-for="(assessment) in allAssessments.slice(0, allAssessments.length - 1)"
              :key="assessment.assessmentID"
              class="has-text-right">-
            </td>
          </template>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.discount, $userInfo.locale) }}</td>
        </tr>
        <tr v-if="value.excess && value.excessWithGst">
          <td>Excess</td>
          <template v-if="allAssessments.length > 1">
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
            <td v-for="(assessment) in allAssessments.slice(0, allAssessments.length - 1)"
              :key="assessment.assessmentID"
              class="has-text-right">-
            </td>
          </template>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
        </tr>
        <tr class="has-border-bottom-4"
          :class="[`summary-background-primary-${$theme.name}`]">
          <td>GST <span class="subtext">({{ value.gstRate }}%)</span></td>
          <template v-if="allAssessments.length > 1">
            <td class="has-text-right">
              <span v-if="mainQuoteTotalGst > 0">+</span> {{ $filters.formatCurrency(mainQuoteTotalGst, $userInfo.locale) }}
            </td>
            <td v-for="(assessment) in allAssessments.slice(1, allAssessments.length)"
              :key="assessment.assessmentID"
              class="has-text-right">+ {{ $filters.formatCurrency(getAssessmentTotal(assessment) * (value.gstRate/100), $userInfo.locale) }}
            </td>
          </template>
          <td class="has-text-right">
            <span v-if="invTotalExGst > 0">+</span>{{ $filters.formatCurrency(invTotalExGst * (value.gstRate/100), $userInfo.locale) }}
          </td>
        </tr>
        <tr v-if="showInvAmtIncGst"
          :class="`summary-background-text-${$theme.name}`">
          <td>Total <span class="subtext">(Inc GST)</span></td>
          <template v-if="allAssessments.length > 1">
            <td class="has-text-right">{{ $filters.formatCurrency(mainQuoteTotalIncGst, $userInfo.locale) }}</td>
            <td v-for="(assessment) in allAssessments.slice(1, allAssessments.length)"
              :key="assessment.assessmentID"
              class="has-text-right">{{ $filters.formatCurrency(getAssessmentTotal(assessment) * (value.gstRate/100 + 1), $userInfo.locale) }}
            </td>
          </template>
          <td class="has-text-right">{{ $filters.formatCurrency(invTotalExGst * (value.gstRate/100 + 1), $userInfo.locale) }}</td>
        </tr>
        <tr v-if="value.ownersContribution">
          <td>Owner Cont. <span class="subtext">(Inc GST)</span></td>
          <template v-if="allAssessments.length > 1">
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.ownersContribution * (value.gstRate/100 + 1), $userInfo.locale) }}</td>
            <td v-for="(assessment) in allAssessments.slice(0, allAssessments.length - 1)"
              :key="assessment.assessmentID"
              class="has-text-right">-
            </td>
          </template>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.ownersContribution * (value.gstRate/100 + 1), $userInfo.locale) }}</td>
        </tr>
        <tr v-if="value.excess && !value.excessWithGst">
          <td>Excess</td>
          <template v-if="allAssessments.length > 1">
            <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
            <td v-for="(assessment) in allAssessments.slice(0, allAssessments.length - 1)"
              :key="assessment.assessmentID"
              class="has-text-right">-
            </td>
          </template>
          <td class="has-text-right has-text-danger">- {{ $filters.formatCurrency(value.excess, $userInfo.locale) }}</td>
        </tr>
        <tr :class="`summary-background-success-${$theme.name}`">
          <th>Total Payable <span class="subtext">(Inc GST)</span></th>
          <template v-if="allAssessments.length > 1">
            <th class="has-text-right">{{ $filters.formatCurrency(mainQuoteTotalPayable, $userInfo.locale) }}</th>
            <th v-for="(assessment) in allAssessments.slice(1, allAssessments.length)"
              :key="assessment.assessmentID"
              class="has-text-right">{{ $filters.formatCurrency(getAssessmentTotal(assessment) * (value.gstRate/100 + 1), $userInfo.locale) }}
            </th>
          </template>
          <th class="has-text-right">
            {{ $filters.formatCurrency(totalPayableIncGst, $userInfo.locale) }}
          </th>
        </tr>
      </tfoot>
      <tbody class="has-bottom-border-only no-border-last-row">
        <tr v-for="(itemType) in companyItemTypes"
          :key="itemType.itemCategoryId">
          <td>
            {{ getDisplayName(itemType.itemCategoryType) }}
          </td>
          <td v-for="assessment in allAssessments"
            :key="assessment.assessmentID"
            class="has-text-right"> {{ $filters.formatNumber(getAssessmentTotal(assessment, itemType.itemCategoryType), $userInfo.locale) }}</td>
          <td v-if="allAssessments.length > 1"
            class="has-text-right">
            {{ $filters.formatNumber(getGrandAssessmentTotal(itemType.itemCategoryType), $userInfo.locale) }}
          </td>
          <td v-if="allAssessments.length === 0"
            class="has-text-right">{{ $filters.formatNumber(0, $userInfo.locale) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { ItemCategoryTypes } from '@/enums'
import { QuoteAssessmentMixin } from './mixins'

export default {
  name: 'QuoteDetailAssessmentSummary',
  mixins: [QuoteAssessmentMixin],
  props: {
    value: null
  },
  data() {
    return {
      draftAssessment: null
    }
  },
  computed: {
    companyItemTypes() {
      return this.$company.itemCategories.filter((i) => {
        if (!i.enabled) return false
        if (!this.$labourTypes || !Array.isArray(this.$labourTypes)) return true
        const labourType = this.$labourTypes.find((t) => t.labourTypeCode === i.itemCategoryType)
        if (!labourType) return true
        return labourType.active !== false && labourType.enabled !== false
      })
    },
    visibleItemTypeCodes() {
      return new Set(this.companyItemTypes.map((i) => i.itemCategoryType))
    },
    allAssessments() {
      const all = this.value.quoteAssessments.filter((a) => a.deleted !== true)
      if (this.draftAssessment) {
        all.push(this.draftAssessment)
      }
      return all
    },
    isEstimage() {
      return this.value.isEstimage
    },
    totalExGst() {
      return this.getGrandAssessmentTotal()
    },
    invTotalExGst() {
      const excess = this.value.excess ?? 0
      const discount = this.value.discount ?? 0

      return !this.value.excessWithGst ? this.totalExGst - discount : this.totalExGst - (excess + discount)
    },
    totalPayableIncGst() {
      const total = this.invTotalExGst * (this.value.gstRate/100 + 1)
      const ownerContributionWithGST = this.value.ownersContribution * (this.value.gstRate/100 + 1)
      const res = this.value.excessWithGst ? total - ownerContributionWithGST : total - this.value.excess - ownerContributionWithGST
      return res
    },
    showInvAmtIncGst() {
      return this.value.ownersContribution || this.value.excess || this.value.discount
    },
    grandSuppTotalExGst() {
      let grandSuppTotalExGst = 0
      for (var i=1; i< this.allAssessments.length; i++) {
        grandSuppTotalExGst = grandSuppTotalExGst + this.getAssessmentTotal(this.allAssessments[i])
      }
      return grandSuppTotalExGst
    },
    grandSuppTotalIncGst() {
      return this.grandSuppTotalExGst * (this.value.gstRate/100 + 1)
    },
    mainQuoteTotalGst() {
      return (this.invTotalExGst - this.grandSuppTotalExGst) * (this.value.gstRate/100)
    },
    mainQuoteTotalIncGst() {
      return this.invTotalExGst * (this.value.gstRate/100 + 1) - this.grandSuppTotalIncGst
    },
    mainQuoteTotalPayable() {
      return this.totalPayableIncGst - this.grandSuppTotalIncGst
    }
  },
  created() {
    this.createDraftAssessment()
  },
  mounted() {},
  methods: {
    getAssessmentTotal(assessment, itemCategoryType = null) {
      if (assessment.authorisedAssessment) {
        let items = itemCategoryType
          ? assessment.authorisedAssessment.assessmentQuoteItems.filter((i) => i.itemType === itemCategoryType)
          : assessment.authorisedAssessment.assessmentQuoteItems.filter((i) => this.visibleItemTypeCodes.has(i.itemType))
        return this.getAssessmentItemsDeltaTotal(items, this.value.quotingMethod, this.isEstimage)
      } else if (assessment.submittedAssessment) {
        let items = itemCategoryType
          ? assessment.submittedAssessment.assessmentQuoteItems.filter((i) => i.itemType === itemCategoryType)
          : assessment.submittedAssessment.assessmentQuoteItems.filter((i) => this.visibleItemTypeCodes.has(i.itemType))
        return this.getAssessmentItemsDeltaTotal(items, this.value.quotingMethod, this.isEstimage)
      }
    },
    getGrandAssessmentTotal(itemCategoryType = null) {
      return this.allAssessments.reduce((acc, assessment) => {
        return acc + this.getAssessmentTotal(assessment, itemCategoryType)
      }, 0)
    },
    getQuoteTotal(itemCategoryType = null) {
      if (itemCategoryType === null) {
        return this.value.totalExGst
      }
      switch (itemCategoryType) {
        case ItemCategoryTypes.RR:
          return this.value.rrTotal
        case ItemCategoryTypes.RWA:
          return this.value.rwaTotal
        case ItemCategoryTypes.REP:
          return this.value.repairTotal
        case ItemCategoryTypes.PAINT:
          return this.value.paintTotal
        case ItemCategoryTypes.FIBER:
          return this.value.fgTotal
        case ItemCategoryTypes.CRUSH:
          return this.value.crushTotal
        case ItemCategoryTypes.CD:
          return this.value.cdTotal
        case ItemCategoryTypes.MECH:
          return this.value.mechTotal
        case ItemCategoryTypes.PART:
          return this.value.partTotal
        case ItemCategoryTypes.OPG:
          return this.value.opgTotal
        case ItemCategoryTypes.MISC:
          return this.value.miscTotal
        case ItemCategoryTypes.SUBL:
          return this.value.sublTotal
        case ItemCategoryTypes.PDRRR:
          return this.value.pdrRrTotal
        case ItemCategoryTypes.PDRRWA:
          return this.value.pdrRwaTotal
        default:
          break
      }
    },
    getDisplayName(itemCategoryType) {
      if (this.companyItemTypes) {
        const itemType = this.companyItemTypes.find((i) => i.itemCategoryType === itemCategoryType)
        if (itemType) {
          return itemType.displayName
        }
      }
      return itemCategoryType
    },
    createDraftAssessment() {
      const items = [...this.value.labours, ...this.value.others, ...this.value.parts, ...this.value.opgs, ...this.value.miscs, ...this.value.sublets]
      const itemsChanged = this.itemsChanged(items, this.value.quotingMethod)
      if (itemsChanged.length > 0) {
        this.draftAssessment = this.createAssessment(itemsChanged, this.value.quoteAssessments.length, this.value.assessorName)
      } else {
        this.draftAssessment = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
th.is-subtotal {
  width: 7em;
}

.subtext {
  font-size: 10px;
}

.no-border td,
.no-border th {
  border: none;
}

.has-bottom-border-only td {
  border-top: none;
  border-left: none;
  border-right: none;
}

.no-border-last-row tr:last-child td {
  border: none;
}
</style>
